import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  FileText,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Lock,
  Pencil,
  Receipt,
  RotateCcw,
  Download,
  CheckCircle2,
} from 'lucide-react';
import {
  fetchBureauSouscriptions,
  fetchBureauFactures,
  createBureauDevis,
  fetchBilan,
  fetchExerciceChiffres,
  fetchEvenementsBureau,
  updateBureauDevis,
  facturerBureauDevis,
  createBureauFacture,
  corrigerDestinataireFacture,
  emettreAvoir,
  fetchSponsorPlans,
  marquerFacturePayee,
  urlPdfDevis,
  urlPdfFacture,
  type LigneDocument,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ChampSiret from '../components/ChampSiret';
import SponsorListPage from './SponsorListPage';

const ligneVide: LigneDocument = { designation: '', quantite: 1, prixUnitaire: 0 };

const formVide = {
  sponsorNom: '',
  sponsorEmail: '',
  sponsorAdresse: '',
  sponsorSiret: '',
  modePaiement: 'virement',
  evenementId: '',
  // '' = l'exercice en cours ; l'identifiant de l'exercice rouvert sinon.
  exerciceId: '',
  lignes: [{ ...ligneVide }],
};

const euros = (montant: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant);

export default function BureauFacturationPage() {
  const { isLoading: authLoading, aLeDroit, user } = useAuth();
  // La correction du nom d'un destinataire est ouverte aux administrateurs :
  // le président, et le support de la plateforme qui n'émet rien par ailleurs.
  const estAdmin =
    user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ROLE_SUPER_ADMIN');
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const ongletDemande = params.get('onglet');
  const [onglet, setOnglet] = useState<'repertoire' | 'devis' | 'factures' | 'avoirs'>(
    ongletDemande === 'devis' || ongletDemande === 'factures' || ongletDemande === 'avoirs'
      ? ongletDemande
      : 'repertoire',
  );
  const [form, setForm] = useState(formVide);
  const [formOuvert, setFormOuvert] = useState<null | 'devis' | 'facture'>(null);
  const [devisEnEdition, setDevisEnEdition] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [factureAPayer, setFactureAPayer] = useState<number | null>(null);
  const [referencePaiement, setReferencePaiement] = useState('');

  // Sur la grille des droits, et non sur le seul role : c'est la fonction
  // au bureau qui decide desormais de l'acces a cet ecran, et le serveur
  // refusera l'appel si elle ne le prevoit pas. Deux droits cohabitent :
  // le repertoire (voir_sponsors) et les pieces (voir_facturation).
  const isBureau = aLeDroit('voir_facturation');
  const aAccesRepertoire = aLeDroit('voir_sponsors');

  const souscriptionsQuery = useQuery({
    queryKey: ['bureau-souscriptions'],
    queryFn: () => fetchBureauSouscriptions(),
    enabled: isBureau,
  });

  // Catalogue des prestations : la même offre que celle proposée au sponsor
  // qui souscrit en ligne. Un devis saisi au bureau et une souscription en
  // ligne doivent porter les mêmes libellés et les mêmes tarifs.
  // Le regime du sponsoring decide si un devis se rattache a une festivite :
  // le flag arrive avec le bilan, seule source qui l'expose au bureau.
  const bilanQuery = useQuery({
    queryKey: ['bilan'],
    queryFn: fetchBilan,
    enabled: isBureau,
  });
  const sponsoringParEvenement = bilanQuery.data?.sponsoringParEvenement === true;
  const evenementsQuery = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: isBureau && sponsoringParEvenement,
  });

  // L'exercice rouvert, s'il y en a un : le formulaire propose alors d'y
  // rattacher la pièce — la facture de décembre retrouvée en mars doit
  // entrer dans décembre, pas dans mars.
  const chiffresQuery = useQuery({
    queryKey: ['bureau-exercice-chiffres'],
    queryFn: fetchExerciceChiffres,
    enabled: isBureau,
  });
  const exerciceRouvert = chiffresQuery.data?.rouvert ?? null;

  const plansQuery = useQuery({
    queryKey: ['sponsor-plans'],
    queryFn: fetchSponsorPlans,
    enabled: isBureau,
  });

  const catalogue = useMemo(() => {
    const plans = plansQuery.data;
    if (!plans) return [];

    const groupes: Array<[string, string]> = [
      ['livret', 'Encart livret'],
      ['ecocup', 'Visibilité écocups'],
      ['tshirt', 'Visibilité t-shirts'],
    ];

    return groupes.flatMap(([cle, prefixe]) =>
      ((plans[cle] ?? []) as Array<{ key: string; label: string; montant: number }>).map((p) => ({
        cle: `${cle}:${p.key}`,
        designation: `${prefixe} - ${p.label}`,
        montant: p.montant,
      })),
    );
  }, [plansQuery.data]);

  const facturesQuery = useQuery({
    queryKey: ['bureau-factures'],
    queryFn: () => fetchBureauFactures(),
    enabled: isBureau,
  });

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['bureau-souscriptions'] });
    queryClient.invalidateQueries({ queryKey: ['bureau-factures'] });
  };

  const reussite = (message: string) => {
    setNotice({ type: 'success', message });
    setFormOuvert(null);
    setDevisEnEdition(null);
    setForm(formVide);
    rafraichir();
  };

  const echec = (error: Error) => setNotice({ type: 'error', message: error.message });

  const creerDevis = useMutation({
    mutationFn: () => createBureauDevis(payloadForm()),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const modifierDevis = useMutation({
    mutationFn: () => updateBureauDevis(devisEnEdition as number, payloadForm()),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const creerFacture = useMutation({
    mutationFn: () => createBureauFacture(payloadForm()),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const facturer = useMutation({
    mutationFn: (id: number) => facturerBureauDevis(id),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const avoir = useMutation({
    mutationFn: (id: number) => emettreAvoir(id),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  // Coquille dans le nom du client : la fiche se corrige, le PDF se
  // regénère — mêmes lignes, même numéro. Rien de comptable ne bouge.
  const corrigerNom = useMutation({
    mutationFn: ({ id, nom }: { id: number; nom: string }) =>
      corrigerDestinataireFacture(id, nom),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  // Encaissement hors ligne — virement, chèque, espèces. Les règlements par
  // carte passent par Stripe, qui bascule le statut de lui-même.
  const payer = useMutation({
    mutationFn: ({ id, reference }: { id: number; reference: string }) =>
      marquerFacturePayee(id, { referencePaiement: reference || undefined }),
    onSuccess: (d) => {
      reussite(d.message ?? 'Facture marquée payée.');
      setFactureAPayer(null);
      setReferencePaiement('');
    },
    onError: echec,
  });

  const payloadForm = () => ({
    sponsorNom: form.sponsorNom,
    sponsorEmail: form.sponsorEmail,
    sponsorAdresse: form.sponsorAdresse,
    sponsorSiret: form.sponsorSiret,
    modePaiement: form.modePaiement,
    evenementId: form.evenementId === '' ? null : Number(form.evenementId),
    exerciceId: form.exerciceId === '' ? undefined : Number(form.exerciceId),
    lignes: form.lignes,
  });

  const majLigne = (index: number, champ: keyof LigneDocument, valeur: string) => {
    const lignes = [...form.lignes];
    lignes[index] = {
      ...lignes[index],
      [champ]: champ === 'designation' ? valeur : Number(valeur.replace(',', '.')) || 0,
    };
    setForm({ ...form, lignes });
  };

  const totalForm = form.lignes.reduce((t, l) => t + (l.quantite || 0) * (l.prixUnitaire || 0), 0);

  const ouvrirEditionDevis = (devis: any) => {
    setForm({
      sponsorNom: devis.sponsor?.nom ?? '',
      sponsorEmail: devis.sponsor?.email ?? '',
      sponsorAdresse: devis.sponsor?.adresse ?? '',
      sponsorSiret: devis.sponsor?.siret ?? '',
      modePaiement: 'virement',
      evenementId: '',
      exerciceId: '',
      lignes: (devis.lignes?.length ? devis.lignes : [{ ...ligneVide }]).map((l: any) => ({
        designation: l.designation,
        quantite: l.quantite,
        prixUnitaire: l.prixUnitaire,
      })),
    });
    setDevisEnEdition(devis.id);
    setFormOuvert('devis');
    setNotice(null);
  };

  if (authLoading || souscriptionsQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isBureau && !aAccesRepertoire) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Cette page dépend de votre fonction au bureau : un administrateur peut ouvrir
              les sponsors ou la facturation dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Un onglet demandé sans le droit correspondant retombe sur ce que le
  // membre peut voir : pas d'écran vide sur un lien profond.
  const ongletActif =
    onglet === 'repertoire'
      ? (aAccesRepertoire ? 'repertoire' : 'devis')
      : isBureau
        ? onglet
        : 'repertoire';

  const souscriptions = souscriptionsQuery.data?.souscriptions ?? [];
  const factures = facturesQuery.data?.factures ?? [];
  const enCours = creerDevis.isPending || modifierDevis.isPending || creerFacture.isPending;

  // Les avoirs ont leur propre onglet : ils ne se creent pas, ils resultent
  // de l'annulation d'une facture.
  const documents = factures.filter((f: any) =>
    ongletActif === 'avoirs' ? f.type === 'avoir' : f.type !== 'avoir',
  );

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Building2 className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Sponsors</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Le répertoire des sponsors et leurs pièces : devis, factures et avoirs.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-gray-200 overflow-x-auto">
        {([
          ...(aAccesRepertoire ? [{ value: 'repertoire', label: 'Répertoire', icone: Building2 } as const] : []),
          ...(isBureau
            ? [
                { value: 'devis', label: 'Devis', icone: FileText } as const,
                { value: 'factures', label: 'Factures', icone: Receipt } as const,
                { value: 'avoirs', label: 'Avoirs', icone: RotateCcw } as const,
              ]
            : []),
        ] as const).map((o) => (
          <button
            key={o.value}
            onClick={() => setOnglet(o.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              ongletActif === o.value ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 dark:text-gray-400'
            }`}
          >
            <o.icone className="w-4 h-4 inline mr-1" /> {o.label}
          </button>
        ))}
      </div>

      <div className={`flex flex-wrap justify-end gap-2 ${ongletActif === 'avoirs' || !isBureau ? 'hidden' : ''}`}>
        <button
          onClick={() => {
            setForm(formVide);
            setDevisEnEdition(null);
            setFormOuvert(formOuvert === 'devis' ? null : 'devis');
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau devis
        </button>
        <button
          onClick={() => {
            setForm(formVide);
            setDevisEnEdition(null);
            setFormOuvert(formOuvert === 'facture' ? null : 'facture');
          }}
          className="btn-outline inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouvelle facture
        </button>
      </div>

      {formOuvert && (
        <div className="card lg:p-5 space-y-4">
          <h2 className="font-semibold">
            {devisEnEdition !== null
              ? 'Modifier le devis'
              : formOuvert === 'devis'
                ? 'Nouveau devis'
                : 'Nouvelle facture'}
          </h2>

          {formOuvert === 'facture' && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Une facture est définitive dès son émission : elle ne pourra plus être modifiée,
              seulement annulée par un avoir. Vérifiez les montants avant de valider.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <input
              className="input"
              placeholder="Nom du sponsor"
              value={form.sponsorNom}
              onChange={(e) => setForm({ ...form, sponsorNom: e.target.value })}
            />
            <input
              className="input"
              type="email"
              placeholder="Email du sponsor"
              value={form.sponsorEmail}
              onChange={(e) => setForm({ ...form, sponsorEmail: e.target.value })}
            />
            <input
              className="input"
              placeholder="Adresse"
              value={form.sponsorAdresse}
              onChange={(e) => setForm({ ...form, sponsorAdresse: e.target.value })}
            />
            <ChampSiret
              value={form.sponsorSiret}
              onChange={(v) => setForm({ ...form, sponsorSiret: v })}
              onEntreprise={(entreprise) => {
                // Ce qui a deja ete saisi fait foi : la recherche complete,
                // elle ne corrige pas.
                setForm((precedent) => ({
                  ...precedent,
                  sponsorNom: precedent.sponsorNom || entreprise.nom || '',
                  sponsorAdresse: precedent.sponsorAdresse || entreprise.adresse || '',
                }));
              }}
              // La recherche ne remplace pas une saisie manuelle. Ce bouton
              // permet de le demander explicitement, sans recopier à la main.
              onReprendreNom={(nom) => setForm((precedent) => ({ ...precedent, sponsorNom: nom }))}
            />

            {/* En régime « sponsoring par événement » — un club vend son
                tournoi là où un comité vend l'année — le devis se rattache à
                la festivité parrainée, et le sponsor entre dans son bilan. */}
            {sponsoringParEvenement && (
              <select
                className="input"
                value={form.evenementId}
                onChange={(e) => setForm({ ...form, evenementId: e.target.value })}
                aria-label="Événement parrainé"
              >
                <option value="">Événement parrainé — aucun</option>
                {(evenementsQuery.data?.evenements ?? []).map((ev: any) => (
                  <option key={ev.id} value={ev.id}>{ev.nom}</option>
                ))}
              </select>
            )}

            {/* Pendant une fenêtre de correction, la pièce peut viser l'année
                rouverte : c'est tout l'objet de la réouverture. Le choix
                n'apparaît que lorsqu'un exercice est effectivement rouvert. */}
            {exerciceRouvert && (
              <div>
                <select
                  className="input"
                  value={form.exerciceId}
                  onChange={(e) => setForm({ ...form, exerciceId: e.target.value })}
                  aria-label="Exercice de rattachement"
                >
                  <option value="">Exercice {chiffresQuery.data?.anneeN ?? 'en cours'}</option>
                  <option value={exerciceRouvert.id}>
                    Exercice {exerciceRouvert.annee} — rouvert pour correction
                  </option>
                </select>
                {form.exerciceId !== '' && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    Cette pièce entrera dans l'exercice {exerciceRouvert.annee}. Pour que
                    l'encaissement y compte aussi, datez le règlement dans cette année-là au
                    moment de marquer la facture payée.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Lignes</p>
              {catalogue.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choisissez une prestation du catalogue, ou saisissez librement.
                </p>
              )}
            </div>

            {form.lignes.map((ligne, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                {/* Le catalogue reprend l'offre proposée aux sponsors en ligne.
                    Il pré-remplit désignation et tarif ; le champ reste
                    librement modifiable, une prestation hors offre devant
                    rester possible. */}
                {catalogue.length > 0 && (
                  <select
                    className="input col-span-12 lg:col-span-3 text-sm"
                    value=""
                    onChange={(e) => {
                      const choix = catalogue.find((p) => p.cle === e.target.value);
                      if (!choix) return;
                      setForm((precedent) => {
                        const lignes = [...precedent.lignes];
                        lignes[index] = {
                          ...lignes[index],
                          designation: choix.designation,
                          prixUnitaire: choix.montant,
                        };
                        return { ...precedent, lignes };
                      });
                    }}
                  >
                    <option value="">Catalogue…</option>
                    {catalogue.map((p) => (
                      <option key={p.cle} value={p.cle}>
                        {p.designation}
                        {p.montant > 0 ? ` — ${p.montant} €` : ' — montant libre'}
                      </option>
                    ))}
                  </select>
                )}

                <input
                  className={`input col-span-12 ${catalogue.length > 0 ? 'lg:col-span-3' : 'lg:col-span-6'}`}
                  placeholder="Désignation"
                  value={ligne.designation}
                  onChange={(e) => majLigne(index, 'designation', e.target.value)}
                />
                <input
                  className="input col-span-3 lg:col-span-2"
                  type="number"
                  min={1}
                  placeholder="Qté"
                  value={ligne.quantite}
                  onChange={(e) => majLigne(index, 'quantite', e.target.value)}
                />
                <input
                  className="input col-span-5 lg:col-span-2"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Prix unitaire"
                  value={ligne.prixUnitaire}
                  onChange={(e) => majLigne(index, 'prixUnitaire', e.target.value)}
                />
                <div className="col-span-3 lg:col-span-1 text-sm text-right text-gray-700 dark:text-gray-300">
                  {euros((ligne.quantite || 0) * (ligne.prixUnitaire || 0))}
                </div>
                <button
                  onClick={() =>
                    setForm({ ...form, lignes: form.lignes.filter((_, i) => i !== index) })
                  }
                  disabled={form.lignes.length === 1}
                  className="col-span-1 text-red-600 hover:text-red-700 disabled:opacity-30"
                  aria-label="Supprimer la ligne"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => setForm({ ...form, lignes: [...form.lignes, { ...ligneVide }] })}
              className="text-sm text-primary-700 inline-flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Ajouter une ligne
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">Total</span>
            <span className="text-lg font-bold text-primary-700">{euros(totalForm)}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                if (devisEnEdition !== null) modifierDevis.mutate();
                else if (formOuvert === 'devis') creerDevis.mutate();
                else creerFacture.mutate();
              }}
              disabled={enCours || totalForm <= 0 || !form.lignes.some((l) => l.designation.trim())}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              {devisEnEdition !== null
                ? 'Enregistrer les corrections'
                : formOuvert === 'devis'
                  ? 'Créer le devis'
                  : 'Émettre la facture'}
            </button>
            <button
              onClick={() => {
                setFormOuvert(null);
                setDevisEnEdition(null);
              }}
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {ongletActif === 'repertoire' ? (
        // Le répertoire vit dans sa page dédiée : ici il s'affiche en onglet,
        // sans son grand titre — celui de la page suffit.
        <SponsorListPage enOnglet />
      ) : ongletActif === 'devis' ? (
        <div className="grid grid-cols-1 gap-3">
          {souscriptions.filter((s: any) => s.devis).length === 0 ? (
            <p className="text-gray-500 text-sm dark:text-gray-400">Aucun devis pour le moment.</p>
          ) : (
            souscriptions
              .filter((s: any) => s.devis)
              .map((s: any) => {
                const verrouille = s.devis.statut !== 'en_attente' || s.devis.valideLe;
                return (
                  <div key={s.devis.id} className="card p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="text-sm space-y-1">
                      <p className="font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
                        {s.devis.numero}
                        {verrouille && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:text-gray-300">
                            <Lock className="w-3 h-3" /> Accepté, non modifiable
                          </span>
                        )}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">{s.sponsor?.nom} — {s.sponsor?.email}</p>
                      <p className="text-gray-900 font-medium dark:text-gray-100">{euros(s.montantTotal ?? 0)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 shrink-0">
                      {s.devis.pdfUrl && (
                        <a href={urlPdfDevis(s.devis.id)} target="_blank" rel="noreferrer" className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm dark:text-gray-400">
                          <Download className="w-4 h-4" /> PDF
                        </a>
                      )}
                      {!verrouille && (
                        <button
                          onClick={() => ouvrirEditionDevis({ ...s.devis, sponsor: s.sponsor })}
                          className="text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 text-sm"
                        >
                          <Pencil className="w-4 h-4" /> Modifier
                        </button>
                      )}
                      {!s.facture && (
                        <button
                          onClick={() => facturer.mutate(s.devis.id)}
                          disabled={facturer.isPending}
                          className="text-green-700 hover:text-green-800 inline-flex items-center gap-1 text-sm"
                        >
                          <Receipt className="w-4 h-4" /> Facturer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {facturesQuery.isPending ? (
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          ) : documents.length === 0 ? (
            <p className="text-gray-500 text-sm dark:text-gray-400">
              {ongletActif === 'avoirs'
                ? 'Aucun avoir émis. Un avoir se crée depuis l\'onglet Factures, en annulant une facture.'
                : 'Aucune facture pour le moment.'}
            </p>
          ) : (
            documents.map((f: any) => (
              <div key={f.id} className="card p-4 flex flex-wrap lg:items-center lg:justify-between gap-3">
                <div className="text-sm space-y-1">
                  <p className="font-semibold text-gray-900 flex flex-wrap items-center gap-2 dark:text-gray-100">
                    {f.numero}
                    {f.type === 'avoir' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Avoir</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:text-gray-300">
                        <Lock className="w-3 h-3" /> Émise, non modifiable
                      </span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-800">{f.statut}</span>
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    {f.sponsor?.nom} — {f.sponsor?.email}
                    {estAdmin && f.type !== 'avoir' && (
                      <button
                        type="button"
                        title="Corriger le nom du destinataire (coquille) — le PDF sera regénéré"
                        onClick={() => {
                          const nom = window.prompt(
                            `Nom corrigé du destinataire de la facture ${f.numero} ?\nLa fiche du client portera le nouveau nom et le PDF sera regénéré.`,
                            f.sponsor?.nom ?? ''
                          );
                          if (nom && nom.trim() && nom.trim() !== f.sponsor?.nom) {
                            corrigerNom.mutate({ id: f.id, nom: nom.trim() });
                          }
                        }}
                        className="ml-2 text-primary-700 hover:underline text-xs dark:text-primary-300"
                      >
                        corriger le nom
                      </button>
                    )}
                  </p>
                  <p className="text-gray-900 font-medium dark:text-gray-100">{euros(f.souscription?.montantTotal ?? 0)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 shrink-0">
                  {f.pdfUrl && (
                    <a href={urlPdfFacture(f.id)} target="_blank" rel="noreferrer" className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm dark:text-gray-400">
                      <Download className="w-4 h-4" /> PDF
                    </a>
                  )}
                  {f.type !== 'avoir' && f.statut !== 'payee' && f.statut !== 'annulee' && (
                    <button
                      onClick={() => {
                        setFactureAPayer(factureAPayer === f.id ? null : f.id);
                        setReferencePaiement('');
                      }}
                      className="text-green-700 hover:text-green-800 inline-flex items-center gap-1 text-sm"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Marquer payée
                    </button>
                  )}
                  {f.type !== 'avoir' && f.statut !== 'annulee' && (
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Émettre un avoir annulant la facture ${f.numero} ? La facture restera dans la numérotation, comme l'exige la réglementation.`
                          )
                        ) {
                          avoir.mutate(f.id);
                        }
                      }}
                      disabled={avoir.isPending}
                      className="text-amber-700 hover:text-amber-800 inline-flex items-center gap-1 text-sm"
                    >
                      <RotateCcw className="w-4 h-4" /> Émettre un avoir
                    </button>
                  )}
                </div>

                {/* Saisie de la référence d'encaissement, dépliée au clic. */}
                {factureAPayer === f.id && (
                  <div className="lg:col-span-full w-full flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                    <input
                      className="input flex-1 min-w-[12rem]"
                      placeholder="Référence du règlement (n° de chèque, virement…)"
                      value={referencePaiement}
                      onChange={(e) => setReferencePaiement(e.target.value)}
                    />
                    <button
                      onClick={() => payer.mutate({ id: f.id, reference: referencePaiement })}
                      disabled={payer.isPending}
                      className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 disabled:opacity-50"
                    >
                      {payer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                      Confirmer l'encaissement
                    </button>
                    <button
                      onClick={() => setFactureAPayer(null)}
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
